/* The on-demand course's own pages (Property Cashflow For Life): Home, Start here, Recordings, Pick
   your patch and Resources, from PCFL v3 (reference/pcfl-v3/source/styles.css) in the hub's product
   frame. Its look is the Accelerator's on purpose (CLAUDE.md "Look and copy"): off-white page, white
   cards, the 2 greens, Inter Tight headings and Inter text, with v3's own tints in the tokens file.
   v3's faint grey never carries small text here; its places use the soft text instead. No board
   draws these pages: on a laptop (1440) Home and Start here use the width, Home in 2 columns, and the
   lists (Recordings, Pick your patch) keep v3's single reading column; below 1100px and on a phone
   (390) every page is 1 column, in v3's own order. */
@import '@fontsource/inter-tight/latin-600.css';

.od { display: flex; flex-direction: column; gap: 28px; min-width: 0; color: var(--acc-ink); font-family: var(--acc-body-font); }
.od-head { display: flex; flex-direction: column; gap: 8px; max-width: 780px; }
.od-kicker { font-size: 13px; font-weight: 600; color: var(--acc-green-2); }
.od-h1 {
  margin: 0; font-family: var(--acc-heading-font); font-weight: 700; font-size: 34px; line-height: 1.12;
  letter-spacing: -0.02em; text-wrap: balance;
}
.od-lede { margin: 0; font-size: 15.5px; line-height: 1.6; color: var(--acc-soft); max-width: 62ch; }
.od-card {
  background: var(--acc-card); border: 1px solid var(--acc-rule); border-radius: 14px;
  box-shadow: 0 1px 2px var(--pcfl-shadow-a), 0 6px 22px var(--pcfl-shadow-b);
}
.od-pad { padding: 28px 32px; }
.od-small { margin: 0; font-size: 13px; line-height: 1.5; color: var(--acc-soft); }
.od-section-h { margin: 0 0 12px; font-family: var(--acc-heading-font); font-weight: 600; font-size: 18px; letter-spacing: -0.01em; }
.od-panel-h {
  margin: 0; font-family: var(--acc-heading-font); font-weight: 600; font-size: 15px; letter-spacing: -0.01em;
  display: flex; align-items: center; gap: 4px 8px; flex-wrap: wrap;
}
.od-msg { margin: 8px 0 0; font-size: 14px; line-height: 1.5; color: var(--acc-ink); }
.od-empty {
  margin: 0; padding: 22px; border: 1px dashed var(--pcfl-line-2); border-radius: 14px;
  font-size: 14.5px; line-height: 1.5; color: var(--acc-soft);
}

/* Buttons */
.od-btn {
  border: 1px solid transparent; border-radius: 10px; padding: 11px 18px; font-family: var(--acc-body-font);
  font-weight: 600; font-size: 14.5px; line-height: 1.2; display: inline-flex; align-items: center; justify-content: center;
  gap: 8px; text-decoration: none; white-space: nowrap; transition: background 0.15s, border-color 0.15s;
}
.pp .od-btn--primary { background: var(--acc-green); color: var(--on-dark); box-shadow: 0 2px 8px var(--pcfl-btn-shadow); }
.pp .od-btn--primary:hover { background: var(--pcfl-green-deep); color: var(--on-dark); }
.pp .od-btn--quiet { background: var(--acc-card); color: var(--acc-ink); border-color: var(--pcfl-line-2); }
.pp .od-btn--quiet:hover { border-color: var(--acc-green-2); color: var(--acc-green); }
.od-btn--sm { padding: 7px 12px; font-size: 13px; border-radius: 8px; }
.od-btn[disabled] { opacity: 0.6; }
.pp .od-textlink { display: inline-block; margin-top: 12px; font-size: 14px; font-weight: 600; color: var(--acc-green); text-underline-offset: 3px; }

/* Chips */
.od-chip {
  display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 600; border-radius: 20px;
  padding: 2px 9px; white-space: nowrap; line-height: 1.6;
}
.od-chip--done { color: var(--acc-green); background: var(--pcfl-green-tint); }
.od-chip--todo, .od-chip--soon { color: var(--acc-soft); background: var(--pcfl-chip); }
.od-chip--big { font-size: 13.5px; padding: 6px 12px; }

/* The pentagon */
.od-pent { display: block; width: 100%; height: auto; overflow: visible; fill: none; stroke-linecap: round; stroke-linejoin: round; flex-shrink: 0; }
.od-pent-base { stroke: var(--pcfl-unlit); }
.od-pent-side { stroke: var(--acc-green); }
.od-pent:not(.od-pent--labels) .od-pent-side--current { opacity: 0.35; }
.od-pent--dark .od-pent-base { stroke: var(--on-dark-28); }
.od-pent--dark .od-pent-side { stroke: var(--pcfl-accent); }
.od-pent-label { font-family: var(--acc-body-font); font-size: 17px; font-weight: 500; stroke: none; }
.od-pent-label--done { fill: var(--acc-green); }
.od-pent-label--current { fill: var(--acc-ink); font-weight: 700; }
.od-pent-label--todo { fill: var(--acc-soft); }

/* A recording's thumbnail */
.od-thumb { position: relative; aspect-ratio: 16 / 9; border-radius: 9px; overflow: hidden; background: var(--pcfl-green-deep); min-width: 0; }
.od-thumb-mark { position: absolute; left: 50%; top: 50%; width: 26%; transform: translate(-50%, -50%); display: block; }
.od-thumb-label { position: absolute; left: 9px; top: 7px; right: 8px; font-size: 11.5px; font-weight: 700; line-height: 1.25; color: var(--on-dark); }
.od-thumb-dur {
  position: absolute; right: 7px; bottom: 7px; font-size: 10.5px; font-weight: 600; color: var(--on-dark);
  background: var(--pcfl-dur-bg); border-radius: 4px; padding: 1px 6px; font-variant-numeric: tabular-nums;
}
.od-thumb-seen {
  position: absolute; left: 7px; bottom: 7px; width: 20px; height: 20px; border-radius: 50%; background: var(--pcfl-accent);
  color: var(--pcfl-green-deep); display: flex; align-items: center; justify-content: center;
}
.od-thumb--small .od-thumb-label { font-size: 10px; left: 6px; top: 5px; }
.od-thumb--small .od-thumb-dur { font-size: 9.5px; right: 5px; bottom: 5px; }
.od-thumb--small .od-thumb-seen { width: 17px; height: 17px; left: 5px; bottom: 5px; }
.od-thumb--off .od-thumb-mark { opacity: 0.45; }
/* Adam's own thumbnail filling the frame (decision 47, 12 Oct), the length and the tick over it */
.od-thumb-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; }
.od-thumb--off.od-thumb--picture .od-thumb-img { opacity: 0.6; }

/* Home */
.od-layout, .od-layout .pp-band, .od-layout .pp-band-col { gap: 24px; }
.od-where { display: grid; grid-template-columns: 250px minmax(0, 1fr); gap: 28px; align-items: center; }
.od-where-key { margin: 6px 0 0; font-size: 12px; line-height: 1.4; text-align: center; color: var(--acc-soft); }
.od-where-text { display: flex; flex-direction: column; align-items: flex-start; min-width: 0; }
.od-where-h { margin: 2px 0 8px; font-family: var(--acc-heading-font); font-weight: 700; font-size: 26px; line-height: 1.15; letter-spacing: -0.02em; }
.od-where-line { margin: 0; font-size: 15px; line-height: 1.6; color: var(--acc-soft); }
.od-status {
  margin-top: 14px; padding: 9px 12px; border-radius: 9px; font-size: 14px; font-weight: 600; line-height: 1.45;
  color: var(--acc-green); background: var(--pcfl-green-tint); align-self: stretch;
}
.od-where-btn { margin-top: 16px; }
.od-next-cards { display: flex; flex-direction: column; gap: 16px; }
.od-next .od-section-h { margin-bottom: 12px; }
.od-wk { padding: 20px 22px; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.od-wk-h { margin: 0; font-family: var(--acc-heading-font); font-weight: 600; font-size: 18px; line-height: 1.25; letter-spacing: -0.01em; }
.od-wk-line { margin: 0; font-size: 14px; line-height: 1.5; color: var(--acc-soft); }
.od-wk-act { margin-top: auto; padding-top: 14px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.od-wk-rec { display: grid; grid-template-columns: 120px minmax(0, 1fr); gap: 14px; align-items: center; margin-top: 6px; }
.od-wk-rec .od-wk-h { font-size: 16px; }
.od-wk-rec-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.od-bar { height: 5px; margin-top: 14px; border-radius: 3px; background: var(--acc-rule); overflow: hidden; }
.od-bar span { display: block; height: 100%; border-radius: 3px; background: var(--acc-green-2); }

/* Homework */
.od-hw-head { display: flex; align-items: center; gap: 4px 10px; flex-wrap: wrap; margin-bottom: 14px; }
.od-hw-head .od-panel-h { font-size: 17px; }
.od-hw-group { margin-bottom: 14px; }
.od-hw-group-h {
  margin: 0 0 8px; font-family: var(--acc-body-font); font-size: 13px; font-weight: 600; color: var(--acc-soft);
  display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap;
}
.od-hw-group-h span { font-weight: 400; }
.od-check {
  display: flex; align-items: flex-start; gap: 12px; padding: 12px 14px; margin-bottom: 8px; border: 1px solid var(--acc-rule);
  border-radius: 10px; background: var(--acc-card); cursor: pointer; font-size: 14.5px; line-height: 1.45;
}
.od-check input { width: 19px; height: 19px; margin: 1px 0 0; accent-color: var(--acc-green); flex-shrink: 0; }
.od-check--on { border-color: var(--pcfl-check-on); background: var(--pcfl-green-tint); }

/* Start here */
.od-rail { display: flex; gap: 6px; flex-wrap: wrap; }
.od-rail-step {
  flex: 1 1 120px; min-width: 0; display: flex; flex-direction: column; text-align: left; padding: 9px 12px;
  border: 1px solid var(--acc-rule); border-radius: 10px; background: var(--acc-card); color: var(--acc-soft);
  font-size: 12.5px; line-height: 1.35;
}
.od-rail-step b { display: flex; align-items: center; gap: 4px; margin-bottom: 2px; font-size: 11px; font-weight: 600; color: var(--acc-soft); }
.od-rail-step--on { border-color: var(--acc-green); color: var(--acc-ink); box-shadow: inset 0 0 0 1px var(--acc-green); }
.od-rail-step--done b { color: var(--acc-green); }
.od-step { display: flex; flex-direction: column; }
.od-step .od-kicker { margin-bottom: 6px; }
.od-step-h { margin: 0 0 12px; font-family: var(--acc-heading-font); font-weight: 700; font-size: 24px; letter-spacing: -0.02em; }
.od-md { max-width: 70ch; margin-bottom: 18px; font-size: 15.5px; line-height: 1.6; }
.od-md p { margin: 0 0 14px; }
.od-md p:last-child, .od-md ul:last-child { margin-bottom: 0; }
.od-md ul { margin: 0 0 14px 20px; padding: 0; }
.od-md li { margin-bottom: 7px; }
.od-md strong { font-weight: 600; }
.od-step-link { align-self: flex-start; }
.od-foot-nav {
  display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap;
  margin-top: 26px; padding-top: 20px; border-top: 1px solid var(--acc-rule);
}
.od-foot-right { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; justify-content: flex-end; }
.od-gate { font-size: 13px; line-height: 1.5; color: var(--acc-soft); }

/* A recording inside a page: the player and its chapters */
.od-block { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr); gap: 22px; align-items: start; }
.od-block-loading { min-height: 220px; }
.od-block-chapters { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.od-chapters { display: flex; flex-direction: column; }
.od-chap {
  display: grid; grid-template-columns: 52px minmax(0, 1fr) auto; gap: 10px; align-items: baseline; padding: 10px 6px;
  background: none; border: none; border-bottom: 1px solid var(--acc-rule); border-radius: 6px; text-align: left;
  font-size: 14px; color: var(--acc-ink);
}
.od-chap:hover { background: var(--pcfl-hover); }
.od-chap--on, .od-chap--on:hover { background: var(--pcfl-green-tint); }
.od-chap-no { font-family: var(--acc-heading-font); font-weight: 600; font-size: 13px; color: var(--acc-soft); }
.od-chap-time { font-size: 12.5px; color: var(--acc-soft); font-variant-numeric: tabular-nums; }
.od-chap--on .od-chap-no, .od-chap--on .od-chap-time { color: var(--acc-green); }

/* Files */
.od-files { display: flex; flex-direction: column; gap: 8px; }
.od-file {
  display: flex; align-items: center; gap: 12px; padding: 10px 12px; border: 1px solid var(--acc-rule);
  border-radius: 10px; background: var(--acc-card);
}
.od-file-icon { display: flex; color: var(--acc-green); flex-shrink: 0; }
.od-file--locked .od-file-icon { color: var(--acc-amber); }
.od-file-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; font-size: 14px; }
.od-file-title { font-weight: 600; }
.od-file-note, .od-file-when { font-size: 12.5px; line-height: 1.45; color: var(--acc-soft); }
.od-file .pp-res-act { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; flex-shrink: 0; }

/* Recordings */
.od-tools { display: flex; flex-direction: column; gap: 12px; }
.od-search { position: relative; display: block; max-width: 520px; color: var(--acc-soft); }
.od-search svg { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); }
.od-search input {
  width: 100%; padding: 12px 14px 12px 40px; border-radius: 10px; border: 1px solid var(--pcfl-line-2);
  background: var(--acc-card); color: var(--acc-ink); font-size: 16px;
}
.od-search input::placeholder { color: var(--acc-soft); }
.od-search input:focus { border-color: var(--acc-green-2); outline: none; box-shadow: 0 0 0 3px var(--pcfl-focus); }
.od-chips { display: flex; gap: 6px; flex-wrap: wrap; }
.od-filter {
  padding: 6px 12px; border-radius: 20px; border: 1px solid var(--acc-rule); background: var(--acc-card);
  font-size: 13px; color: var(--acc-soft);
}
.od-filter--on { background: var(--acc-green); border-color: var(--acc-green); color: var(--on-dark); }
.od-mod { display: flex; flex-direction: column; gap: 12px; }
.od-mod-h { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 6px; }
.od-mod-h .od-pent { width: 34px; }
.od-mod-titles { display: flex; flex-direction: column; min-width: 0; }
.od-mod-no { font-size: 12.5px; font-weight: 600; letter-spacing: 0.02em; color: var(--acc-soft); }
.od-mod-name { margin: 0; font-family: var(--acc-heading-font); font-weight: 600; font-size: 18px; letter-spacing: -0.01em; }
.od-mod-right { margin-left: auto; }
.od-recs, .od-patch { max-width: 920px; }
.od-rec-list { display: flex; flex-direction: column; gap: 10px; }
.od-rec {
  display: grid; grid-template-columns: 160px minmax(0, 1fr); gap: 16px; align-items: center; padding: 12px;
  border-radius: 14px; text-decoration: none;
}
.pp .od-rec { color: var(--acc-ink); }
.pp a.od-rec:hover { border-color: var(--acc-green-2); }
.od-rec--locked { border: 1px dashed var(--acc-rule); }
.od-rec-text { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.od-rec-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; font-size: 12.5px; color: var(--acc-soft); }
.od-rec-h { font-family: var(--acc-heading-font); font-weight: 600; font-size: 16.5px; line-height: 1.3; letter-spacing: -0.01em; text-wrap: pretty; }
.od-rec-hits { display: flex; flex-direction: column; margin-top: 3px; font-size: 13px; color: var(--acc-soft); }
.od-rec-hits b { font-weight: 600; color: var(--acc-ink); }
.od-mod-hw { margin-top: 4px; }

/* Pick your patch */
.od-ep-open { display: flex; flex-direction: column; }
.od-ep-open-head { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 14px; }
.od-ep-open-h { margin: 0; font-family: var(--acc-heading-font); font-weight: 600; font-size: 19px; letter-spacing: -0.01em; }
.od-ep-open-head .od-chip { margin-left: auto; }
.od-eps { display: flex; flex-direction: column; gap: 10px; }
.od-ep {
  display: grid; grid-template-columns: 44px minmax(0, 1fr) auto; gap: 14px; align-items: center; padding: 14px 16px;
  width: 100%; text-align: left; font: inherit; color: var(--acc-ink);
}
.od-ep:hover { border-color: var(--acc-green-2); }
.od-ep--on { border-color: var(--acc-green); box-shadow: inset 0 0 0 1px var(--acc-green); }
.od-ep-n { font-family: var(--acc-heading-font); font-weight: 700; font-size: 22px; text-align: center; color: var(--acc-soft); }
.od-ep--done .od-ep-n { color: var(--acc-green); }
.od-ep-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.od-ep-h { font-family: var(--acc-heading-font); font-weight: 600; font-size: 16px; letter-spacing: -0.01em; }
.od-ep-line { font-size: 13.5px; line-height: 1.45; color: var(--acc-soft); }
.od-ep-tick {
  width: 26px; height: 26px; border-radius: 13px; background: var(--pcfl-green-tint); color: var(--acc-green);
  display: flex; align-items: center; justify-content: center;
}
.od-ep-go { font-size: 13px; font-weight: 600; color: var(--acc-soft); }
.od-sheet { display: flex; flex-direction: column; gap: 8px; }
.od-player--none .pp-player-line { color: var(--on-dark-80); }

/* Resources: the bonus */
.od-tool { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 16px; align-items: center; padding: 20px 22px; }
.od-tool-h { margin: 0; font-family: var(--acc-heading-font); font-weight: 600; font-size: 17px; letter-spacing: -0.01em; }
.od-tool-line { margin: 3px 0 0; font-size: 14px; line-height: 1.5; color: var(--acc-soft); }

/* Between a laptop and a phone: 1 column, in v3's order; Up next's cards side by side */
@media (max-width: 1099px) {
  .od-block { grid-template-columns: minmax(0, 1fr); }
  .od-next-cards { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .od-next-cards > :last-child:nth-child(odd) { grid-column: 1 / -1; }
}

/* A phone */
@media (max-width: 760px) {
  .od { gap: 22px; }
  .od-h1 { font-size: 27px; }
  .od-pad { padding: 20px 18px; }
  .od-where { grid-template-columns: minmax(0, 1fr); gap: 10px; }
  .od-where-pent { width: 100%; max-width: 300px; margin: 0 auto; }
  .od-where-h { font-size: 23px; }
  .od-next-cards { display: flex; flex-direction: column; }
  .od-rec { grid-template-columns: 112px minmax(0, 1fr); gap: 12px; padding: 10px; }
  .od-rec-h { font-size: 15px; }
  .od-thumb-label { font-size: 10px; left: 6px; top: 5px; }
  .od-thumb-dur { font-size: 9.5px; }
  .od-tool { grid-template-columns: minmax(0, 1fr); }
  .od-tool .od-btn { justify-self: start; }
  .od-step-h { font-size: 22px; }
  .od-foot-nav .od-btn--primary { flex-grow: 1; }
  .od-foot-right { width: 100%; justify-content: stretch; }
  .od-foot-right .od-gate { width: 100%; }
  .od-file { flex-wrap: wrap; }
  .od-file .pp-res-act { width: 100%; align-items: flex-start; }
}
